import { useEffect } from 'react';
import { syncPending } from '../services/offlineQueue';
import { useDialog } from '../context/DialogContext';
import { useAuth } from '../context/AuthContext';
import { warmOcrCache } from '../utils/receiptOcr';

/**
 * Uploads receipts queued offline: on app start, when the connection comes
 * back, and when the app returns to the foreground. Also pre-caches the OCR
 * engine so receipts can be read offline. Renders nothing.
 */
function OfflineSync() {
  const { toast } = useDialog();
  const { user } = useAuth();
  const ocrEnabled = user?.ocr_enabled !== false;

  useEffect(() => {
    if (!ocrEnabled) return undefined;
    // Let the app settle first; this downloads ~7 MB once
    const timer = setTimeout(warmOcrCache, 5000);
    return () => clearTimeout(timer);
  }, [ocrEnabled]);

  useEffect(() => {
    const run = async () => {
      const uploaded = await syncPending();
      if (uploaded > 0) {
        toast(`${uploaded} offline receipt${uploaded > 1 ? 's' : ''} uploaded`);
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };

    run();
    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [toast]);

  return null;
}

export default OfflineSync;
